import { post, get, patch } from "./divertidogsApiUtils";
import {
  CiudadResumida,
  PaisResumido,
  DepartamentoResumido,
  Sector,
} from "./paisesServicios";
import { MascotaResumida } from "./mascotasServicios";
import {
  objectToFormData,
  objectToFormDataOptionalValues,
} from "../utils/formDataUtils";
import { useQuery } from "@tanstack/react-query";
import { AgendamientoResumido } from "./agendamientosServicios";

export enum TipoDocumento {
  Cedula = "CC",
  TarjetaIdentidad = "TI",
  CedulaExtranjeria = "CE",
  Pasaporte = "PAS",
}

export const prettyTipoDocumento = (tipoDocumento: TipoDocumento): string => {
  switch (tipoDocumento) {
    case TipoDocumento.Cedula:
      return "Cédula";
    case TipoDocumento.TarjetaIdentidad:
      return "Tarjeta de Identidad";
    case TipoDocumento.CedulaExtranjeria:
      return "Cédula de Extranjería";
    case TipoDocumento.Pasaporte:
      return "Pasaporte";
  }
};

export enum CanalMarketing {
  Instagram = "IG",
  Facebook = "FB",
  Recomendacion = "REC",
  PublicidadImpresa = "PI",
}

export const prettyCanalMarketing = (
  canalMarketing: CanalMarketing
): string => {
  switch (canalMarketing) {
    case CanalMarketing.Instagram:
      return "Instagram";
    case CanalMarketing.Facebook:
      return "Facebook";
    case CanalMarketing.Recomendacion:
      return "Recomendación";
    case CanalMarketing.PublicidadImpresa:
      return "Publicidad Impresa";
  }
};

export interface RespuestaLeerPropietarios {
  id: string;
  documento: string;
  nombres: string;
  celular: string;
  mascotas: string[];
  direccion?: string;
  email: string;
}

export interface NuevoPropietario {
  primerNombre: string;
  segundoNombre?: string;
  primerApellido: string;
  segundoApellido?: string;
  contrato?: File;
  firma?: string;
  tipoDocumento: TipoDocumento;
  documento: string;
  ciudadExpedicion?: string;
  email: string;
  celular: string;
  nombresContactoAlterno?: string;
  celularContactoAlterno?: string;
  idPaisResidencia?: string;
  idDepartamentoResidencia?: string;
  idCiudadResidencia?: string;
  direccionResidencia?: string;
  idSectorResidencia?: string;
  canalMarketing?: CanalMarketing;
  esTurista: boolean;
  idPaisProveniencia?: string;
  idCiudadProveniencia?: string;
  fechaNacimiento?: Date;
}

export const leerPropietarios = async (): Promise<
  RespuestaLeerPropietarios[] | undefined
> => {
  return get<Array<RespuestaLeerPropietarios>>("propietarios");
};

export const QUERY_KEY_LISTAR_PROPIETARIOS = "QUERY_KEY_LISTAR_PROPIETARIOS";

export const useLeerPropietarios = () => {
  return useQuery(
    [QUERY_KEY_LISTAR_PROPIETARIOS],
    async () => await leerPropietarios()
  );
};

export interface RespuestaCrearPropietario {
  id: string;
}

export const crearPropietario = async (
  nuevoPropietario: NuevoPropietario
): Promise<RespuestaCrearPropietario> => {
  const formData = objectToFormData(nuevoPropietario);

  return post<FormData, RespuestaCrearPropietario>("propietarios", formData);
};

export interface RespuestaBuscarPropietario {
  id: string;
  primerNombre: string;
  segundoNombre?: string;
  primerApellido: string;
  segundoApellido?: string;
  tipoDocumento: TipoDocumento;
  documento: string;
  ciudadExpedicion: string;
  email: string;
  celular: string;
  nombresContactoAlterno?: string;
  celularContactoAlterno?: string;
  paisResidencia: PaisResumido;
  departamentoResidencia: DepartamentoResumido;
  ciudadResidencia?: CiudadResumida;
  direccionResidencia?: string;
  sectorResidencia?: Sector;
  canalMarketing?: CanalMarketing;
  esTurista: boolean;
  paisProveniencia?: PaisResumido;
  ciudadProveniencia?: CiudadResumida;
  fechaNacimiento?: string;
  mascotas: MascotaResumida[];
  tieneContrato: boolean;
}

export const buscarPropietario = async (id: string) => {
  return get<RespuestaBuscarPropietario>("propietarios/" + id);
};

export const QUERY_BUSCAR_PROPIETARIO = "QUERY_BUSCAR_PROPIETARIO";

export const useBuscarPropietario = (id: string) =>
  useQuery([QUERY_BUSCAR_PROPIETARIO], async () => await buscarPropietario(id));

export interface Archivo {
  contenido: string;
  nombre?: string;
  extension?: string;
  fecha: string;
}

export const buscarContrato = async (idPropietario: string) => {
  return get<Archivo>("propietarios/" + idPropietario + "/contrato");
};

export interface ModificacionPropietario {
  primerNombre?: string;
  segundoNombre?: string;
  primerApellido?: string;
  segundoApellido?: string;
  contrato?: File;
  eliminarContrato?: boolean;
  firma?: string;
  tipoDocumento?: TipoDocumento;
  documento?: string;
  ciudadExpedicion?: string;
  email?: string;
  celular?: string;
  nombresContactoAlterno?: string;
  celularContactoAlterno?: string;
  idPaisResidencia?: string;
  idDepartamentoResidencia?: string;
  idCiudadResidencia?: string;
  direccionResidencia?: string;
  idSectorResidencia?: string;
  canalMarketing?: CanalMarketing;
  esTurista?: boolean;
  idPaisProveniencia?: string;
  idCiudadProveniencia?: string;
  fechaNacimiento?: string;
}

export const modificarPropietario = async (
  idPropietario: string,
  modificacionPropietario: ModificacionPropietario
): Promise<undefined> => {
  const formData = objectToFormDataOptionalValues(modificacionPropietario);

  return patch<FormData, undefined>("propietarios/" + idPropietario, formData);
};

export const QUERY_KEY_LISTAR_AGENDAMIENTOS_ACTIVOS =
  "QUERY_KEY_LISTAR_AGENDAMIENTOS_ACTIVOS";

const listarAgendamientosActivos = async (
  idPropietario: string
): Promise<AgendamientoResumido[] | undefined> => {
  return get<AgendamientoResumido[]>(
    `propietarios/${idPropietario}/agendamientos`
  );
};

export const useListarAgendamientosActivos = (
  idPropietario: string | undefined
) =>
  useQuery({
    queryKey: [QUERY_KEY_LISTAR_AGENDAMIENTOS_ACTIVOS],
    queryFn: async () => await listarAgendamientosActivos(idPropietario!!),
    enabled: !!idPropietario,
    initialData: [],
  });
